/** @format */

import { useCallback, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { setBos } from "redux/actions/bosAction";
import {
  deletePencairanSumberDanaBos,
  deleteVerifikasiSumberDanaBos,
  editPengajuanSumberDanaBos,
  editVerifikasiSumberDanaBos,
  getPlanningAll,
} from "services/v2/planningservice";

const CACHE_TTL = 30 * 60 * 1000;

export const usePencairanBos = (auth: any) => {
  const dispatch = useDispatch();

  const {
    verifikasiSumberDanaBos,
    pengajuanSumberDanaBos,
    penyaluranSumberDanaBos,
    cacheTime,
  } = useSelector(
    (state: any) => ({
      verifikasiSumberDanaBos: state.bos?.verifikasiSumberDanaBos || [],
      pengajuanSumberDanaBos: state.bos?.pengajuanSumberDanaBos || [],
      penyaluranSumberDanaBos: state.bos?.penyaluranSumberDanaBos || [],
      cacheTime: state.bos?.cacheTime || {},
    }),
    shallowEqual,
  );

  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(
    async (
      type: "verifikasi" | "pengajuan" | "penyaluran",
      force: boolean = false,
    ) => {
      const now = Date.now();

      const cacheMap: any = {
        verifikasi: {
          data: verifikasiSumberDanaBos,
          time: cacheTime?.verifikasi,
        },
        pengajuan: {
          data: pengajuanSumberDanaBos,
          time: cacheTime?.pengajuan,
        },
        penyaluran: {
          data: penyaluranSumberDanaBos,
          time: cacheTime?.penyaluran,
        },
      };

      const cached = cacheMap[type];

      /** cek redux cache */
      if (
        !force &&
        cached?.data?.length > 0 &&
        cached?.time &&
        now - cached.time < CACHE_TTL
      ) {
        return;
      }

      setLoading(true);

      try {
        let endpoint = "";
        let payload: any = { tahun: auth?.isTahun };

        if (type === "verifikasi") {
          endpoint = "portal-bos/verifikasi/list";
        }

        if (type === "pengajuan") {
          endpoint = "portal-bos/pengajuan/list";
          payload.status = 2;
        }

        if (type === "penyaluran") {
          endpoint = "portal-bos/pengajuan/list";
          payload.status = 3;
        }

        const result = (await getPlanningAll(endpoint, payload)) || [];

        dispatch(
          setBos({
            [`${type}SumberDanaBos`]: result,
            cacheTime: {
              ...cacheTime,
              [type]: Date.now(),
            },
          }),
        );
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [
      auth?.isTahun,
      dispatch,
      cacheTime,
      verifikasiSumberDanaBos,
      pengajuanSumberDanaBos,
      penyaluranSumberDanaBos,
    ],
  );

  const updateVerfikasiPencairan = useCallback(
    async (payload: any) => {
      setLoading(true);
      try {
        const res = await editVerifikasiSumberDanaBos(payload);
        fetchData("verifikasi", true);
        return res;
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    },
    [fetchData],
  );

  const updatePengajuanPenyaluran = useCallback(
    async (payload: any) => {
      setLoading(true);
      try {
        const res = await editPengajuanSumberDanaBos(payload);
        // Baris berpindah dari tab Pengajuan ke Penyaluran → refresh keduanya
        fetchData("pengajuan", true);
        fetchData("penyaluran", true);
        return res;
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    },
    [fetchData],
  );

  const deleteDataPencairan = useCallback(
    async (payload: any, type: "verifikasi" | "pengajuan" | "penyaluran") => {
      setLoading(true);

      try {
        const res =
          type === "verifikasi"
            ? await deleteVerifikasiSumberDanaBos(payload?.dokumen_pencairan_id)
            : await deletePencairanSumberDanaBos(payload);
        if (type === "verifikasi") {
          fetchData(type, true);
        } else {
          // Cancel memindah status baris antar tab Pengajuan/Penyaluran
          fetchData("pengajuan", true);
          fetchData("penyaluran", true);
        }
        return res;
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    },
    [fetchData],
  );

  return {
    loading,
    verifikasiSumberDanaBos,
    pengajuanSumberDanaBos,
    penyaluranSumberDanaBos,
    getDataVerification: (force = false) => fetchData("verifikasi", force),
    getDataPengajuan: (force = false) => fetchData("pengajuan", force),
    getDataPenyaluran: (force = false) => fetchData("penyaluran", force),
    updateVerfikasiPencairan,
    updatePengajuanPenyaluran,
    deleteDataPencairan,
  };
};
